import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { TodoItemResponse } from '@/lib/api';
import { TodoItemRow } from './TodoItemRow';
import { resolveItemDrop } from '../lib/dnd';

interface ListFlatViewProps {
  /** Filtered top-level items to display, already in section-flatten order. */
  items: TodoItemResponse[];
  /** The FULL active top-level set — the drop resolver works over this (not the
   *  filtered view) so a hidden row is never disturbed and section membership is
   *  read correctly. */
  allItems: TodoItemResponse[];
  /** Manager (owner/admin/self) — only a manager can drag-reorder the flat list;
   *  members/viewers see it read-only. */
  isManager: boolean;
  canEditItem: (item: TodoItemResponse) => boolean;
  /** Reorder items within a section (same-section drop). */
  onReorder: (orderedIds: string[]) => void;
  /** Move an item into another section at a position (cross-section drop). */
  onMove: (itemId: string, destSectionId: string | null, orderedIds: string[]) => void;
}

/**
 * The flat "List" view of a shared list: every top-level task in one list, in the
 * SAME order as the Sections view (top-to-bottom), sub-tasks nested + locked under
 * their parent. It's a live projection of the section order — dragging writes
 * THROUGH to the sections (reusing {@link resolveItemDrop}, the same resolver the
 * Sections view uses): a same-section drop reorders within that section; a
 * cross-section drop moves the task into the drop-target's section (manager-only,
 * mirroring a between-sections drag — the row's section chip shows the change).
 */
export function ListFlatView({
  items,
  allItems,
  isManager,
  canEditItem,
  onReorder,
  onMove,
}: ListFlatViewProps) {
  const [activeItem, setActiveItem] = useState<TodoItemResponse | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  function handleDragEnd(e: DragEndEvent) {
    setActiveItem(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const result = resolveItemDrop({
      activeId: String(active.id),
      overId: String(over.id),
      items: allItems,
      isManager,
    });
    if (!result) return;
    if (result.kind === 'reorder') onReorder(result.order);
    else onMove(result.itemId, result.destSection, result.order);
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>No tasks match this view.</p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e: DragStartEvent) =>
        setActiveItem(items.find((i) => i.itemId === String(e.active.id)) ?? null)
      }
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveItem(null)}
    >
      <SortableContext items={items.map((i) => i.itemId)} strategy={verticalListSortingStrategy}>
        <div className="space-y-1">
          {items.map((item) => (
            <TodoItemRow
              key={item.itemId}
              item={item}
              canEdit={canEditItem(item)}
              // Manager-only drag on the flat list; members reorder within their
              // sections via the Sections view.
              canManageSection={isManager}
              // Sections are flattened here — surface each task's section path.
              groupContext="project"
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay>
        {activeItem ? (
          <div className="rounded-md border bg-background px-3 py-2 text-sm shadow-lg">
            {activeItem.title}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
